/**
 * File: clientQuoteUtils.ts
 * Path: apps/admin/src/shared/utils/clientQuoteUtils.ts
 * Description: A saved quote as the client record shows it. The `quotes`
 * collection, its writes and its rules belong to the quotes feature; this is
 * a read-only view of the fields the client page relies on, read
 * defensively so a missing field never breaks the page.
 */

import { readMillis, readNumber, readText } from "@idk/config";

export const QUOTE_STATUSES = [
  "sent",
  "accepted",
  "declined",
  "expired",
] as const;

export type ClientQuoteStatus = (typeof QUOTE_STATUSES)[number];

export const QUOTE_STATUS_LABELS: Record<ClientQuoteStatus, string> = {
  sent: "Sent",
  accepted: "Accepted",
  declined: "Declined",
  expired: "Expired",
};

export interface ClientQuote {
  id: string;
  reference: string;
  source: "website" | "admin";
  clientId: string | null;
  leadId: string | null;
  name: string;
  planName: string;
  priceLine: string;
  total: number | null;
  status: ClientQuoteStatus;
  contractId: string | null;
  createdAt: number | null;
  holdUntil: number | null;
}

export const toClientQuote = (
  id: string,
  data: Record<string, unknown>,
): ClientQuote => ({
  id,
  reference: readText(data.reference),
  source: data.source === "website" ? "website" : "admin",
  clientId: readText(data.clientId) || null,
  leadId: readText(data.leadId) || null,
  name: readText(data.name),
  planName: readText(data.planName),
  priceLine: readText(data.priceLine),
  total: readNumber(data.total),
  status: QUOTE_STATUSES.find((s) => s === data.status) ?? "sent",
  contractId: readText(data.contractId) || null,
  createdAt: readMillis(data.createdAt),
  holdUntil: readMillis(data.holdUntil),
});

/**
 * The client's quotes and the ones sent to their lead before they were won,
 * once each (a quote can carry both ids), newest first.
 */
export const mergeQuotes = (
  ...lists: ReadonlyArray<readonly ClientQuote[]>
): ClientQuote[] => {
  const byId = new Map<string, ClientQuote>();
  lists.flat().forEach((quote) => byId.set(quote.id, quote));
  return [...byId.values()].sort(
    (a, b) => (b.createdAt ?? 0) - (a.createdAt ?? 0),
  );
};
